import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  TextField, 
  IconButton, 
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { getShareInfo, importSharedAi } from '../services/api';

interface ShareInfo {
  aiId: string;
  collectionId: string;
  aiTitle: string;
  aiDescription: string;
  aiDomain: string;
  collectionVersion: string;
  collectionInstruction?: string;
  createdBy: string;
  expiresAt: string;
}

const ImportAi = () => {
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [shareCode, setShareCode] = useState('');
  const [password, setPassword] = useState('');
  const [newAiTitle, setNewAiTitle] = useState('');
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const steps = ['Enter Share Codes', 'Review AI Details', 'Import AI'];

  const handleVerifyShare = async () => {
    if (!shareCode.trim() || !password.trim()) {
      setError('Please enter both share code and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getShareInfo(shareCode.trim(), password.trim());
      setShareInfo(response.data);
      setNewAiTitle(`${response.data.aiTitle} (Imported)`);
      setActiveStep(1);
    } catch (err: any) {
      console.error('Error verifying share:', err);
      setError(err.response?.data?.error || 'Failed to verify share codes');
    } finally {
      setLoading(false);
    }
  };

  const handleImportAi = async () => {
    if (!shareCode || !password) return;

    setImporting(true);
    setError(null);

    try {
      await importSharedAi(shareCode, password, newAiTitle);
      setSuccess(true);
      setActiveStep(2);
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/my-ais');
      }, 3000);
    } catch (err: any) {
      console.error('Error importing AI:', err);
      setError(err.response?.data?.error || 'Failed to import AI');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setShareCode('');
    setPassword('');
    setNewAiTitle('');
    setShareInfo(null);
    setError(null);
    setSuccess(false);
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/my-ais')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <DownloadIcon sx={{ mr: 1 }} />
          Import AI
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            AI imported successfully! Redirecting to My AIs...
          </Alert>
        )}

        {/* Step 1: Enter Share Codes */}
        {activeStep === 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Enter Share Codes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the share code and password provided by the AI creator.
            </Typography>
            
            <TextField
              label="Share Code"
              value={shareCode}
              onChange={(e) => setShareCode(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              placeholder="e.g., ai-id-collection-id"
              helperText="The share code contains the AI ID and Collection ID"
            />
            
            <TextField
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              placeholder="e.g., ABC123XYZ"
              helperText="The password provided with the share code"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/my-ais')}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleVerifyShare}
                disabled={loading || !shareCode.trim() || !password.trim()}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <InfoIcon />}
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </Box>
          </>
        )}

        {/* Step 2: Review AI Details */}
        {activeStep === 1 && shareInfo && (
          <>
            <Typography variant="h6" gutterBottom>
              Review AI Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review the AI information and customize the title for your copy.
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Original AI Information:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Title:</strong> {shareInfo.aiTitle}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Domain:</strong> {shareInfo.aiDomain}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Version:</strong> {shareInfo.collectionVersion}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Created by:</strong> {shareInfo.createdBy}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Expires:</strong> {new Date(shareInfo.expiresAt).toLocaleDateString()}
              </Typography>
              {shareInfo.aiDescription && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Description:</strong> {shareInfo.aiDescription}
                </Typography>
              )}
              {shareInfo.collectionInstruction && (
                <Typography variant="body2">
                  <strong>Instructions:</strong> {shareInfo.collectionInstruction}
                </Typography>
              )}
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Note:</strong> For complex AIs with large databases or many Q&A pairs, 
                the import process may take a few minutes to complete. Please be patient while 
                we copy all the data to your account.
              </Typography>
            </Alert>

            <TextField
              label="New AI Title"
              value={newAiTitle}
              onChange={(e) => setNewAiTitle(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              helperText="This will be the title of your imported AI"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                fullWidth
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleImportAi}
                disabled={importing || !newAiTitle.trim()}
                fullWidth
                startIcon={importing ? <CircularProgress size={20} /> : <DownloadIcon />}
              >
                {importing ? 'Importing...' : 'Import AI'}
              </Button>
            </Box>
          </>
        )}

        {/* Step 3: Success */}
        {activeStep === 2 && success && (
          <>
            <Typography variant="h6" gutterBottom color="success.main">
              Import Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              The AI has been successfully imported to your account. You can now find it in your "My AIs" page.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/my-ais')}
              fullWidth
            >
              Go to My AIs
            </Button>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="subtitle2" gutterBottom>
          How AI Import Works:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • Creates a complete copy of the shared AI in your account
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • Includes all Q&A data, instructions, and settings
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • You become the owner of the imported copy
        </Typography>
        <Typography variant="body2">
          • Changes to your copy won't affect the original AI
        </Typography>
      </Paper>
    </Box>
  );
};

export default ImportAi;
